// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Authentication Service
// ═══════════════════════════════════════════════════════════════════════════
// Handles all authentication operations
// ═══════════════════════════════════════════════════════════════════════════

import {
    signInWithPopup,
    signInWithRedirect,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPhoneNumber,
    RecaptchaVerifier,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase.js';

// ─────────────────────────────────────────────────────────────────────────
// AUTH STATE
// ─────────────────────────────────────────────────────────────────────────

let currentUser = null;
let authStateListeners = [];

// Listen to auth state changes
onAuthStateChanged(auth, async (user) => {
    currentUser = user;

    if (user) {
        // Create/update user profile in Firestore
        await ensureUserProfile(user);
    }

    // Notify all listeners
    authStateListeners.forEach(listener => listener(user));
});

export function onAuthChange(callback) {
    authStateListeners.push(callback);
    // Immediately call with current state
    callback(currentUser);

    // Return unsubscribe function
    return () => {
        authStateListeners = authStateListeners.filter(l => l !== callback);
    };
}

export function getCurrentUser() {
    return currentUser;
}

export function isAuthenticated() {
    return currentUser !== null;
}

// ─────────────────────────────────────────────────────────────────────────
// GOOGLE SIGN IN
// ─────────────────────────────────────────────────────────────────────────

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
    prompt: 'select_account'
});

export async function signInWithGoogle() {
    try {
        // Try popup first (works best on desktop)
        const result = await signInWithPopup(auth, googleProvider);
        return { success: true, user: result.user };
    } catch (error) {
        console.error('Google sign-in error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);

        // If popup was blocked or closed, try redirect
        if (error.code === 'auth/popup-blocked' ||
            error.code === 'auth/popup-closed-by-user' ||
            error.code === 'auth/cancelled-popup-request') {
            try {
                // Use redirect as fallback
                await signInWithRedirect(auth, googleProvider);
                return { success: true, pending: true };
            } catch (redirectError) {
                console.error('Redirect sign-in error:', redirectError);
                return {
                    success: false,
                    error: getErrorMessage(redirectError.code)
                };
            }
        }

        // Check for unauthorized domain error
        if (error.code === 'auth/unauthorized-domain') {
            return {
                success: false,
                error: 'This domain is not authorized for sign-in. Please contact the developer.'
            };
        }

        return {
            success: false,
            error: getErrorMessage(error.code)
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────
// EMAIL/PASSWORD SIGN IN
// ─────────────────────────────────────────────────────────────────────────

export async function signInWithEmail(email, password) {
    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return { success: true, user: result.user };
    } catch (error) {
        console.error('Email sign-in error:', error);
        return {
            success: false,
            error: getErrorMessage(error.code)
        };
    }
}

export async function createAccountWithEmail(email, password, displayName) {
    try {
        const result = await createUserWithEmailAndPassword(auth, email, password);

        // Update display name
        if (displayName) {
            await updateProfile(result.user, { displayName });
        }

        return { success: true, user: result.user };
    } catch (error) {
        console.error('Account creation error:', error);
        return {
            success: false,
            error: getErrorMessage(error.code)
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────
// PHONE SIGN IN
// ─────────────────────────────────────────────────────────────────────────

let recaptchaVerifier = null;
let confirmationResult = null;

export function setupRecaptcha(buttonId) {
    if (recaptchaVerifier) {
        recaptchaVerifier.clear();
    }

    recaptchaVerifier = new RecaptchaVerifier(auth, buttonId, {
        size: 'invisible',
        callback: () => {
            // reCAPTCHA solved
        }
    });

    return recaptchaVerifier;
}

export async function sendPhoneVerification(phoneNumber, buttonId) {
    try {
        if (!recaptchaVerifier) {
            setupRecaptcha(buttonId);
        }

        confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
        return { success: true };
    } catch (error) {
        console.error('Phone verification error:', error);
        recaptchaVerifier?.clear();
        recaptchaVerifier = null;
        return {
            success: false,
            error: getErrorMessage(error.code)
        };
    }
}

export async function verifyPhoneCode(code) {
    try {
        if (!confirmationResult) {
            return { success: false, error: 'No verification in progress' };
        }

        const result = await confirmationResult.confirm(code);
        confirmationResult = null;
        return { success: true, user: result.user };
    } catch (error) {
        console.error('Phone code verification error:', error);
        return {
            success: false,
            error: getErrorMessage(error.code)
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────
// SIGN OUT
// ─────────────────────────────────────────────────────────────────────────

export async function logOut() {
    try {
        await signOut(auth);
        return { success: true };
    } catch (error) {
        console.error('Sign out error:', error);
        return { success: false, error: error.message };
    }
}

// ─────────────────────────────────────────────────────────────────────────
// USER PROFILE
// ─────────────────────────────────────────────────────────────────────────

async function ensureUserProfile(user) {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
        // Create new user profile
        await setDoc(userRef, {
            displayName: user.displayName || null,
            email: user.email || null,
            photoURL: user.photoURL || null,
            createdAt: serverTimestamp(),
            lastSeen: serverTimestamp(),
            settings: {
                theme: 'light',
                notifications: true,
                shareAnonymously: false
            }
        });
    } else {
        // Update last seen
        await setDoc(userRef, {
            lastSeen: serverTimestamp()
        }, { merge: true });
    }
}

export async function getUserProfile(userId) {
    const userRef = doc(db, 'users', userId || currentUser?.uid);
    const userSnap = await getDoc(userRef);
    return userSnap.exists() ? userSnap.data() : null;
}

// ─────────────────────────────────────────────────────────────────────────
// ERROR MESSAGES
// ─────────────────────────────────────────────────────────────────────────

function getErrorMessage(errorCode) {
    const messages = {
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/email-already-in-use': 'An account already exists with this email.',
        'auth/weak-password': 'Password should be at least 6 characters.',
        'auth/popup-closed-by-user': 'Sign-in was cancelled.',
        'auth/cancelled-popup-request': 'Sign-in was cancelled.',
        'auth/invalid-verification-code': 'Invalid verification code.',
        'auth/invalid-phone-number': 'Please enter a valid phone number.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
    };

    return messages[errorCode] || 'Something went wrong. Please try again.';
}

export default {
    signInWithGoogle,
    signInWithEmail,
    createAccountWithEmail,
    sendPhoneVerification,
    verifyPhoneCode,
    logOut,
    getCurrentUser,
    isAuthenticated,
    onAuthChange,
    getUserProfile
};
